// Module-level singleton stack of open modal IDs.
//
// When multiple modals are open simultaneously (e.g. "Add Rule" opened on top
// of "Permission Group Detail"), only the topmost should be visible — the rest
// stay mounted (preserving state) but are visually hidden so backdrops do NOT
// stack and parent edges do NOT peek around the nested modal.
//
// useModalStack returns whether the caller's modal is the topmost; Modal.tsx
// uses this to drop overlay + content visibility when stacked behind another.
//
// Subscription is plain pub/sub — no Zustand to keep this primitive lightweight
// and avoid a new global store dependency just for one piece of UI state.
import { useEffect, useState } from "react";

let stack: string[] = [];
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

export function pushModal(id: string) {
  stack = [...stack, id];
  notify();
}

export function popModal(id: string) {
  stack = stack.filter((s) => s !== id);
  notify();
}

export interface ModalStackState {
  /** True when this id is the topmost modal in the stack. */
  isTopmost: boolean;
  /** True when this id is mounted but stacked behind another open modal. */
  isStacked: boolean;
  /** Total open modals (1 = solo, 2+ = nested). */
  total: number;
}

export function useModalStack(id: string, isOpen: boolean): ModalStackState {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((x) => x + 1);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    pushModal(id);
    return () => {
      popModal(id);
    };
  }, [id, isOpen]);

  const idx = stack.indexOf(id);
  const total = stack.length;
  const isTopmost = idx >= 0 && idx === total - 1;
  const isStacked = idx >= 0 && total > 1 && !isTopmost;
  return { isTopmost, isStacked, total };
}
